import { useState } from "react";
import { toast } from "sonner";
import { Play, Download } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { fmtMoney2, fmtNum } from "@/lib/format";

const ENTITIES = {
  transactions: {
    label: "Transactions (bills)",
    dims: [["store_id", "Store ID"], ["store_name", "Store name"], ["channel", "Channel"], ["payment_mode", "Payment mode"], ["new_or_existing", "New / Existing"], ["is_return", "Is return"]],
    metrics: [["count", "Bills"], ["sum_net", "Total Paid (₹)"], ["sum_gross", "Gross (₹)"], ["avg_net", "Avg Total Paid (₹)"], ["unique_customers", "Unique customers"]],
  },
  customers: {
    label: "Customers",
    dims: [["tier", "Tier"], ["city", "City"], ["state", "State"], ["gender", "Gender"], ["home_store_id", "Home store"]],
    metrics: [["count", "Customers"], ["sum_spend", "Lifetime spend (₹)"], ["points_balance", "Points balance"]],
  },
};
const MONEY = new Set(["sum_net", "sum_gross", "avg_net", "sum_spend"]);

const toggle = (list, key) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

function CheckGroup({ title, options, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(([k, label]) => (
          <label key={k} className={`px-3 py-1.5 border text-xs cursor-pointer transition-colors ${value.includes(k) ? "kazo-bg-burgundy border-transparent" : "border-black/15 hover:border-black/40"}`} data-testid={`${testid}-${k}`}>
            <input type="checkbox" className="sr-only" checked={value.includes(k)} onChange={() => onChange(toggle(value, k))} />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
}

function downloadCsv(cols, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [cols.map(([, l]) => esc(l)).join(","), ...rows.map((r) => cols.map(([k]) => esc(r[k])).join(","))].join("\r\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `custom_report_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function CustomReportBuilder() {
  const [entity, setEntity] = useState("transactions");
  const [dims, setDims] = useState(["store_id"]);
  const [metrics, setMetrics] = useState(["count", "sum_net"]);
  const [period, setPeriod] = useState(30);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const cfg = ENTITIES[entity];

  const changeEntity = (e) => {
    setEntity(e);
    setDims([ENTITIES[e].dims[0][0]]);
    setMetrics(ENTITIES[e].metrics.map(([k]) => k).slice(0, 2));
    setRows(null);
  };

  const run = async () => {
    if (!metrics.length) return toast.error("Pick at least one metric");
    setLoading(true);
    try {
      const r = await api.post("/reports/custom", { entity, dimensions: dims, metrics, period_days: period });
      setRows((r.data.rows || []).map((x) => ({ ...(x._id || {}), ...x })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally {
      setLoading(false);
    }
  };

  const shownMetrics = cfg.metrics.filter(([k]) => entity === "customers" || metrics.includes(k));
  const cols = [...cfg.dims.filter(([k]) => dims.includes(k)), ...shownMetrics];
  const fmt = (k, v) => (MONEY.has(k) ? fmtMoney2(v) : typeof v === "number" ? fmtNum(v) : v ?? "—");

  return (
    <div data-testid="custom-report-builder-page">
      <PageHeader title="Custom Report Builder" subtitle="PICK DIMENSIONS · METRICS · PERIOD — LIVE AGGREGATION" />
      <div className="p-8 space-y-6">
        <div className="bg-white border border-black/10 p-6 space-y-5">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Data source</div>
              <select className="k-input !w-auto" value={entity} onChange={(e) => changeEntity(e.target.value)} data-testid="crb-entity">
                {Object.entries(ENTITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {entity === "transactions" && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Period</div>
                <select className="k-input !w-auto" value={period} onChange={(e) => setPeriod(parseInt(e.target.value))} data-testid="crb-period">
                  <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last 365 days</option>
                </select>
              </div>
            )}
          </div>
          <CheckGroup title="Group by (dimensions)" options={cfg.dims} value={dims} onChange={setDims} testid="crb-dim" />
          {entity === "transactions" && <CheckGroup title="Metrics" options={cfg.metrics} value={metrics} onChange={setMetrics} testid="crb-metric" />}
          <div className="flex gap-3">
            <button className="k-btn kazo-bg-burgundy k-btn-sm" onClick={run} disabled={loading} data-testid="crb-run-btn"><Play className="w-3.5 h-3.5" /> {loading ? "Running…" : "Run report"}</button>
            <button className="k-btn k-btn-outline k-btn-sm" onClick={() => downloadCsv(cols, rows || [])} disabled={rows === null} data-testid="crb-export-btn"><Download className="w-3.5 h-3.5" /> CSV</button>
          </div>
        </div>

        {rows !== null && (
          <div className="bg-white border border-black/10 p-5" data-testid="crb-results">
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-3" data-testid="crb-row-count">{fmtNum(rows.length)} ROWS</div>
            {rows.length === 0 ? (
              <div className="py-10 text-center text-sm text-neutral-500" data-testid="crb-empty">No data for this selection yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr>{cols.map(([k, l]) => <th key={k} className={MONEY.has(k) || k === "count" ? "text-right" : ""}>{l}</th>)}</tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>{cols.map(([k]) => <td key={k} className={MONEY.has(k) || k === "count" ? "text-right font-mono" : "text-xs"}>{fmt(k, r[k])}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
